import {
  ChangeDetectorRef,
  OnDestroy,
  Pipe,
  PipeTransform,
} from '@angular/core';
import { Store, select } from '@ngrx/store';
import { AppState, MetaActions, selectCountryCode } from '@qtek/libs/store';
import { conformToMask, getPhoneMask } from '@qtek/shared/utils';
import { BehaviorSubject, Subject, combineLatest } from 'rxjs';
import { filter, first, takeUntil, tap } from 'rxjs/operators';

let isCountryCodesLoaded = false;

@Pipe({
  name: 'phone',
  pure: false,
  standalone: true,
})
export class PhonePipe implements PipeTransform, OnDestroy {
  private destroy$ = new Subject<void>();

  private tel$ = new BehaviorSubject<string | undefined>(undefined);
  private country$ = new BehaviorSubject<string>('');
  private includeCode$ = new BehaviorSubject<boolean>(false);

  private value: string;

  constructor(
    private store: Store<AppState>,
    private cdr: ChangeDetectorRef
  ) {
    const phones$ = this.store.pipe(
      select(selectCountryCode),
      tap(value => {
        if (!value && !isCountryCodesLoaded) {
          isCountryCodesLoaded = true;
          this.store.dispatch(MetaActions.loadMetaCountryCode());
        }
      }),
      filter(value => value?.length > 0),
      first()
    );

    combineLatest([phones$, this.tel$, this.country$, this.includeCode$])
      .pipe(
        takeUntil(this.destroy$),
        filter(([phones]) => Boolean(phones))
      )
      .subscribe(([phones]) => {
        this.value = this.formatPhoneNumber(phones);
        this.cdr.markForCheck();
      });
  }

  transform(tel: string, country: string, includeCode = false): string {
    this.tel$.next(tel);
    this.country$.next(country);
    this.includeCode$.next(includeCode);
    return this.value || tel;
  }

  private formatPhoneNumber(phones: any[]): string {
    const tel = this.tel$.getValue();
    const country = this.country$.getValue();
    const includeCode = this.includeCode$.getValue();

    if (!tel) {
      return '';
    }

    const phoneCountry = phones.find(phone => phone.id === country);
    const value = tel.toString().trim().replace(/^\+/, '');

    if (phoneCountry) {
      return `${includeCode ? '+' + phoneCountry.cd + ' ' : ''}${
        conformToMask(value, getPhoneMask(phoneCountry.mask).mask, {
          guide: false,
        }).conformedValue
      }`;
    }
    const cleanTel = tel.toString().replace(/\D/g, '');

    return this.formatCleanPhoneNumber(cleanTel, tel);
  }

  private formatCleanPhoneNumber(phone: string, fullPhone: string): string {
    if (phone?.length === 11) {
      const countryCode = phone.slice(0, 1);
      const areaCode = phone.slice(1, 4);
      const midSection = phone.slice(4, 7);
      const lastSection = phone.slice(7);
      return `+${countryCode} (${areaCode}) ${midSection}-${lastSection}`;
    } else if (phone?.length === 10) {
      const areaCode = phone.slice(0, 3);
      const midSection = phone.slice(3, 6);
      const lastSection = phone.slice(6);
      return `(${areaCode}) ${midSection}-${lastSection}`;
    } else {
      return fullPhone;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
